import React from 'react';
import ScienceIcon from '@mui/icons-material/Science';
import { Clue, Order, StarterKit } from './types';
import {
  GiJamesBondAperture,
  GiPistolGun,
  GiPublicSpeaker,
  GiUsaFlag,
} from 'react-icons/gi';
import StarBorderPurple500Icon from '@mui/icons-material/StarBorderPurple500';
import PublicIcon from '@mui/icons-material/Public';
import LocalMoviesIcon from '@mui/icons-material/LocalMovies';
import VideocamIcon from '@mui/icons-material/Videocam';
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AlbumIcon from '@mui/icons-material/Album';
import LyricsIcon from '@mui/icons-material/Lyrics';
import { BiCabinet } from 'react-icons/bi';
import { FaBible, FaChessKing, FaChessQueen } from 'react-icons/fa';
import { BsFillMoonStarsFill, BsStars } from 'react-icons/bs';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { CgPokemon } from 'react-icons/cg';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import { TbZodiacAquarius, TbZodiacScorpio } from 'react-icons/tb';
import DiamondIcon from '@mui/icons-material/Diamond';
import ForestIcon from '@mui/icons-material/Forest';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';

type StarterKitIcon =
  | 'GiUsaFlag'
  | 'StarBorderPurple500Icon'
  | 'ScienceIcon'
  | 'GiPublicSpeaker'
  | 'LocalMoviesIcon'
  | 'VideocamIcon'
  | 'MovieCreationIcon'
  | 'SportsFootballIcon'
  | 'MusicNoteIcon'
  | 'AlbumIcon'
  | 'LyricsIcon'
  | 'FaChessKing'
  | 'FaChessQueen'
  | 'FaBible'
  | 'BsStars'
  | 'BsFillMoonStarsFill'
  | 'GavelIcon'
  | 'AccountBalanceIcon'
  | 'CalendarMonthIcon'
  | 'BiCabinet'
  | 'GiJamesBondAperture'
  | 'PublicIcon'
  | 'GiPistolGun'
  | 'CgPokemon'
  | 'VideogameAssetIcon'
  | 'TbZodiacScorpio'
  | 'TbZodiacAquarius'
  | 'DiamondIcon'
  | 'ForestIcon'
  | 'TheaterComedyIcon';

type StarterKitWithIcons = {
  starterKitSet: number;
  starterKitName: string;
  starterKitNameAbbr?: string;
  startIcon: StarterKitIcon;
  endIcon: StarterKitIcon;
  orders: Order[];
  clues: Clue[];
  clue1Label?: string;
  clue2Label?: string;
};

export const STARTER_KITS: StarterKitWithIcons[] = [
  {
    starterKitSet: 1,
    starterKitName: 'State by Capital',
    startIcon: 'GiUsaFlag',
    endIcon: 'StarBorderPurple500Icon',
    orders: ['random', 'alphabetical'],
    clues: ['1 only'],
    clue1Label: 'City',
  },
  {
    starterKitSet: 2,
    starterKitName: 'Capital by State',
    startIcon: 'StarBorderPurple500Icon',
    endIcon: 'GiUsaFlag',
    orders: ['random', 'alphabetical'],
    clues: ['1 only'],
    clue1Label: 'State',
  },
  {
    starterKitSet: 3,
    starterKitName: 'Country by Capital',
    startIcon: 'PublicIcon',
    endIcon: 'StarBorderPurple500Icon',
    orders: ['random', 'alphabetical', 'group'],
    clues: ['1 only'],
    clue1Label: 'Capital',
  },
  {
    starterKitSet: 4,
    starterKitName: 'Capital by Country',
    startIcon: 'StarBorderPurple500Icon',
    endIcon: 'PublicIcon',
    orders: ['random', 'alphabetical', 'group'],
    clues: ['1 only'],
    clue1Label: 'Country',
  },
  {
    starterKitSet: 5,
    starterKitName: 'Chemical Elements',
    startIcon: 'ScienceIcon',
    endIcon: 'ScienceIcon',
    orders: ['random', 'sequence', 'group'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Atomic number',
    clue2Label: 'Chemical symbol',
  },
  {
    starterKitSet: 6,
    starterKitName: 'U.S. Presidents',
    startIcon: 'StarBorderPurple500Icon',
    endIcon: 'GiPublicSpeaker',
    orders: ['random', 'sequence'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Chronological number',
    clue2Label: 'Years',
  },
  {
    starterKitSet: 7,
    starterKitName: 'Best Picture',
    startIcon: 'LocalMoviesIcon',
    endIcon: 'LocalMoviesIcon',
    orders: ['random', 'sequence'],
    clues: ['1 only', '1 then 2'],
    clue1Label: 'Year',
    clue2Label: 'Leading actors',
  },
  {
    starterKitSet: 8,
    starterKitName: 'Best Director',
    startIcon: 'VideocamIcon',
    endIcon: 'MovieCreationIcon',
    orders: ['random', 'sequence'],
    clues: ['1 only', '2 only', '1 then 2'],
    clue1Label: 'Year',
    clue2Label: 'Film',
  },
  {
    starterKitSet: 9,
    starterKitName: 'Super Bowl Winners',
    startIcon: 'SportsFootballIcon',
    endIcon: 'SportsFootballIcon',
    orders: ['random', 'sequence'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Super Bowl Number',
    clue2Label: 'Year',
  },
  {
    starterKitSet: 10,
    starterKitName: 'Grammy Best Album',
    startIcon: 'MusicNoteIcon',
    endIcon: 'AlbumIcon',
    orders: ['random', 'sequence'],
    clues: ['1 only', '1 then 2', '2 then 1'],
    clue1Label: 'Year',
    clue2Label: 'Artist',
  },
  {
    starterKitSet: 11,
    starterKitName: 'Grammy Best Record',
    startIcon: 'MusicNoteIcon',
    endIcon: 'LyricsIcon',
    orders: ['random', 'sequence'],
    clues: ['1 only', '1 then 2', '2 then 1'],
    clue1Label: 'Year',
    clue2Label: 'Artist',
  },
  {
    starterKitSet: 12,
    starterKitName: 'UK Monarchs',
    startIcon: 'FaChessKing',
    endIcon: 'FaChessQueen',
    orders: ['random', 'sequence'],
    clues: ['1 only', '1 then 2', '2 then 1'],
    clue1Label: 'Year',
    clue2Label: 'House',
  },
  {
    starterKitSet: 13,
    starterKitName: 'Bible Books',
    startIcon: 'FaBible',
    endIcon: 'FaBible',
    orders: ['random', 'sequence', 'group'],
    clues: ['1 only', '2 only'],
    clue1Label: 'Preceding Book',
    clue2Label: 'Succeeding Book',
  },
  {
    starterKitSet: 14,
    starterKitName: 'Constellations',
    startIcon: 'BsStars',
    endIcon: 'BsFillMoonStarsFill',
    orders: ['random', 'alphabetical'],
    clues: ['1 only'],
    clue1Label: 'Nickname',
  },
  {
    starterKitSet: 15,
    starterKitName: 'Current Senators',
    startIcon: 'CalendarMonthIcon',
    endIcon: 'AccountBalanceIcon',
    orders: ['random', 'group'],
    clues: ['1 only'],
    clue1Label: 'Description',
  },
  {
    starterKitSet: 16,
    starterKitName: 'State by Senators',
    startIcon: 'GiUsaFlag',
    endIcon: 'AccountBalanceIcon',
    orders: ['random'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Senior senator',
    clue2Label: 'Junior senator',
  },
  {
    starterKitSet: 17,
    starterKitName: 'Current Governors',
    startIcon: 'CalendarMonthIcon',
    endIcon: 'GavelIcon',
    orders: ['random', 'group'],
    clues: ['1 only'],
    clue1Label: 'State',
  },
  {
    starterKitSet: 18,
    starterKitName: 'State by Governor',
    startIcon: 'GiUsaFlag',
    endIcon: 'GiPublicSpeaker',
    orders: ['random'],
    clues: ['1 only'],
    clue1Label: 'Governor',
  },
  {
    starterKitSet: 19,
    starterKitName: 'Current Cabinet',
    startIcon: 'CalendarMonthIcon',
    endIcon: 'BiCabinet',
    orders: ['random', 'group'],
    clues: ['1 only'],
    clue1Label: 'Description (disclaimer: this quiz asks both directions)',
  },
  {
    starterKitSet: 20,
    starterKitName: 'James Bond Movies',
    startIcon: 'GiPistolGun',
    endIcon: 'GiJamesBondAperture',
    orders: ['random', 'group', 'sequence'],
    clues: ['1 only', '1 then 2', '2 then 1'],
    clue1Label: 'Year',
    clue2Label: 'Villain',
  },
  {
    starterKitSet: 21,
    starterKitName: 'Pokemon Starters',
    startIcon: 'CgPokemon',
    endIcon: 'CgPokemon',
    orders: ['random', 'group', 'sequence'],
    clues: ['1 only'],
    clue1Label: 'Description',
  },
  {
    starterKitSet: 22,
    starterKitName: 'SSBU Characters',
    startIcon: 'VideogameAssetIcon',
    endIcon: 'VideogameAssetIcon',
    orders: ['random', 'group'],
    clues: ['1 only'],
    clue1Label: 'Description',
  },
  {
    starterKitSet: 23,
    starterKitName: 'Zodiac Signs',
    startIcon: 'TbZodiacAquarius',
    endIcon: 'TbZodiacScorpio',
    orders: ['random', 'sequence'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Dates',
    clue2Label: 'Nickname',
  },
  {
    starterKitSet: 24,
    starterKitName: 'Birthstones and Anniversaries',
    starterKitNameAbbr: 'B Stones & Anniv',
    startIcon: 'DiamondIcon',
    endIcon: 'DiamondIcon',
    orders: ['random', 'sequence', 'group'],
    clues: ['1 only'],
    clue1Label: 'Description',
  },
  {
    starterKitSet: 25,
    starterKitName: 'National Parks',
    startIcon: 'ForestIcon',
    endIcon: 'ForestIcon',
    orders: ['random', 'group'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Description (geography and other)',
    clue2Label: 'Major attraction',
  },
  {
    starterKitSet: 26,
    starterKitName: 'Shakespeare Plays',
    startIcon: 'TheaterComedyIcon',
    endIcon: 'TheaterComedyIcon',
    orders: ['random', 'group'],
    clues: ['1 only', '2 only', '1 then 2', '2 then 1'],
    clue1Label: 'Major roles',
    clue2Label: 'Setting',
  },
];

export const getStarterKitIcon = (
  starterKitSet: number,
  startOrEnd: 'start' | 'end'
) => {
  const found = STARTER_KITS.find((s) => s.starterKitSet === starterKitSet);
  if (!found) return <></>;
  const icon = startOrEnd === 'start' ? found.startIcon : found.endIcon;
  if (icon === 'GiUsaFlag') {
    return <GiUsaFlag />;
  }
  if (icon === 'PublicIcon') {
    return <PublicIcon />;
  }
  if (icon === 'StarBorderPurple500Icon') {
    return <StarBorderPurple500Icon />;
  }
  if (icon === 'ScienceIcon') {
    return <ScienceIcon />;
  }
  if (icon === 'GiPublicSpeaker') {
    return <GiPublicSpeaker />;
  }
  if (icon === 'LocalMoviesIcon') {
    return <LocalMoviesIcon />;
  }
  if (icon === 'VideocamIcon') {
    return <VideocamIcon />;
  }
  if (icon === 'MovieCreationIcon') {
    return <MovieCreationIcon />;
  }
  if (icon === 'SportsFootballIcon') {
    return <SportsFootballIcon />;
  }
  if (icon === 'MusicNoteIcon') {
    return <MusicNoteIcon />;
  }
  if (icon === 'AlbumIcon') {
    return <AlbumIcon />;
  }
  if (icon === 'LyricsIcon') {
    return <LyricsIcon />;
  }
  if (icon === 'FaChessKing') {
    return <FaChessKing />;
  }
  if (icon === 'FaChessQueen') {
    return <FaChessQueen />;
  }
  if (icon === 'FaBible') {
    return <FaBible />;
  }
  if (icon === 'BsStars') {
    return <BsStars />;
  }
  if (icon === 'BsFillMoonStarsFill') {
    return <BsFillMoonStarsFill />;
  }
  if (icon === 'GavelIcon') {
    return <GavelIcon />;
  }
  if (icon === 'AccountBalanceIcon') {
    return <AccountBalanceIcon />;
  }
  if (icon === 'CalendarMonthIcon') {
    return <CalendarMonthIcon />;
  }
  if (icon === 'BiCabinet') {
    return <BiCabinet />;
  }
  if (icon === 'GiJamesBondAperture') {
    return <GiJamesBondAperture />;
  }
  if (icon === 'GiPistolGun') {
    return <GiPistolGun />;
  }
  if (icon === 'CgPokemon') {
    return <CgPokemon />;
  }
  if (icon === 'VideogameAssetIcon') {
    return <VideogameAssetIcon />;
  }
  if (icon === 'TbZodiacScorpio') {
    return <TbZodiacScorpio />;
  }
  if (icon === 'TbZodiacAquarius') {
    return <TbZodiacAquarius />;
  }
  if (icon === 'DiamondIcon') {
    return <DiamondIcon />;
  }
  if (icon === 'ForestIcon') {
    return <ForestIcon />;
  }
  if (icon === 'TheaterComedyIcon') {
    return <TheaterComedyIcon />;
  }
};

export const getAbbreviatedStarterKitName = (starterKitSet: number) => {
  const found = STARTER_KITS.find((s) => s.starterKitSet === starterKitSet);
  if (!found || !found.starterKitNameAbbr) return null;
  return found.starterKitNameAbbr;
};

export const sortByArrayCompare = (array: string[], a, b) => {
  if (array.indexOf(a) < array.indexOf(b)) {
    return -1;
  }
  if (array.indexOf(a) > array.indexOf(b)) {
    return 1;
  }
  return 0;
};

export const getClueLabels = (starterKitSet: number, clue: 1 | 2) => {
  const found = STARTER_KITS.find((s) => s.starterKitSet === starterKitSet);
  if (!found) return null;
  if (clue === 1) return found.clue1Label;
  if (clue === 2) return found.clue2Label;
  return null;
};
